/**
 * AI CONTEXT
 * Purpose: data/mutation hook for standout tags + moments (stats, sorting, save/create helpers).
 * Reads: evaluation keywords + student moments scoped by student/semester/curriculum.
 * Exposes: sorted tags, auto-selection, custom tag creation, moment save, context helpers.
 * Used by: StandoutMomentsPanel and Teacher StandoutMomentPanel.
 * Refactor: move category-count strategy maps to separate constants module.
 */
import { countBy, groupBy, keyBy, orderBy } from "lodash"
import { useCallback, useMemo } from "react"

import { evaluationKeywordAtoms, standoutMomentAtoms } from "#atoms"
import Firebase from "#classes/Firebase"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  EvaluationKeywordConstructor,
  StandoutCategory,
  StandoutMomentConstructor,
  TagPool,
} from "#customTypes/composedTypes/standoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useFilteredDocs } from "#hooks/jotai/useSubscription"

export type SortMode = "frequency" | "recent" | "oldest" | "az"

export type NewStandoutMomentInput = {
  studentId: Uuid
  semester: string
  curriculumId: Uuid
  createdBy: Uuid
  createdAt: string
  category?: StandoutCategory
  tagIds: Uuid[]
  note?: string
  generatedText?: string
  evaluationId?: Uuid
  classId?: Uuid
  projectId?: Uuid
  classSessionId?: Uuid
}

/**
 * A new quick story. A note alone is enough; tags are optional. Category is
 * stored when the caller has one (including a secret note with no tags).
 * Every row is pending for family review and stamped `source: "jarvis"`.
 * The caller supplies `classSessionId`: the most recent session the student
 * attended. A caller with no session leaves it off.
 */
export const buildNewStandoutMoment = (
  input: NewStandoutMomentInput
): (Omit<StandoutMomentConstructor, "id"> & { id?: Uuid }) | null => {
  const note = input.note?.trim()
  if (input.tagIds.length < 1 && !note) return null

  return {
    studentId: input.studentId,
    semester: input.semester,
    curriculumId: input.curriculumId,
    tagIds: input.tagIds,
    createdBy: input.createdBy,
    createdAt: input.createdAt,
    source: "jarvis",
    familyState: "pending",
    isShared: false,
    ...(input.category ? { category: input.category } : {}),
    ...(note ? { note } : {}),
    ...(input.generatedText?.trim()
      ? { generatedText: input.generatedText.trim() }
      : {}),
    ...(input.evaluationId ? { evaluationId: input.evaluationId } : {}),
    ...(input.classId ? { classId: input.classId } : {}),
    ...(input.projectId ? { projectId: input.projectId } : {}),
    ...(input.classSessionId ? { classSessionId: input.classSessionId } : {}),
  }
}

interface PerCategoryCounts {
  thumbsUpCount: number
  needsWorkCount: number
  secretCount: number
  lastUsedAt: string | null
}

export interface TagWithStats extends EvaluationKeywordConstructor {
  thumbsUpCount: number
  needsWorkCount: number
  secretCount: number
  lastUsedAt: string | null
}

const EMPTY_COUNTS: PerCategoryCounts = {
  thumbsUpCount: 0,
  needsWorkCount: 0,
  secretCount: 0,
  lastUsedAt: null,
}

const CATEGORY_COUNT_KEY: Record<StandoutCategory, keyof PerCategoryCounts> = {
  thumbsUp: "thumbsUpCount",
  needsWork: "needsWorkCount",
  secret: "secretCount",
}

export const OPPOSITE_COUNT_KEY: Partial<
  Record<StandoutCategory, keyof PerCategoryCounts>
> = {
  thumbsUp: "needsWorkCount",
  needsWork: "thumbsUpCount",
}

export function useStandoutMoments(
  studentId: Uuid,
  semester: string,
  curriculumId: Uuid
) {
  const { currentUserId } = useUserContext()

  // Load all active tag suggestions
  const tagQuery = useMemo(
    () => ({
      queries: {
        isActive: { value: true, comparator: COMPARATORS.EQUALS },
      },
    }),
    []
  )

  const allTags = useFilteredDocs<EvaluationKeywordConstructor>(
    evaluationKeywordAtoms,
    DOC_TYPES.EVALUATION_KEYWORDS,
    tagQuery
  )

  // Load standout moments scoped to student + semester + curriculumId
  const momentQuery = useMemo(
    () => ({
      queries: {
        studentId: { value: studentId, comparator: COMPARATORS.EQUALS },
        semester: { value: semester, comparator: COMPARATORS.EQUALS },
        curriculumId: { value: curriculumId, comparator: COMPARATORS.EQUALS },
      },
    }),
    [studentId, semester, curriculumId]
  )

  const studentMoments = useFilteredDocs<StandoutMomentConstructor>(
    standoutMomentAtoms,
    DOC_TYPES.STANDOUT_MOMENTS,
    momentQuery
  )

  const tagsById = useMemo(() => keyBy(allTags, "id"), [allTags])

  // If tag suggestions collection is empty (no seeds), this stays false and
  // the panel's auto-persist will never activate. Acceptable for now — seeding
  // is a prerequisite for the feature. Revisit if we hit this in practice.
  const isFulfilled = allTags.length > 0

  // Per-tag stats split by category
  const tagStats = useMemo(() => {
    const stats: Record<Uuid, PerCategoryCounts> = {}

    for (const moment of studentMoments) {
      if (!moment.category) continue
      const countKey = CATEGORY_COUNT_KEY[moment.category]
      for (const tagId of moment.tagIds ?? []) {
        if (!stats[tagId]) {
          stats[tagId] = { ...EMPTY_COUNTS }
        }
        ;(stats[tagId][countKey] as number)++
        if (
          !stats[tagId].lastUsedAt ||
          moment.createdAt > stats[tagId].lastUsedAt!
        ) {
          stats[tagId].lastUsedAt = moment.createdAt
        }
      }
    }

    return stats
  }, [studentMoments])

  // Tags with split counts attached
  const enrichedTags = useMemo(() => {
    return allTags.map(
      (tag): TagWithStats => ({
        ...tag,
        thumbsUpCount: tagStats[tag.id]?.thumbsUpCount ?? 0,
        needsWorkCount: tagStats[tag.id]?.needsWorkCount ?? 0,
        secretCount: tagStats[tag.id]?.secretCount ?? 0,
        lastUsedAt: tagStats[tag.id]?.lastUsedAt ?? null,
      })
    )
  }, [allTags, tagStats])

  const tagsByPool = useMemo(
    () => groupBy(enrichedTags, "pool"),
    [enrichedTags]
  )

  // Sort tags -- "frequency" uses the count for the active category.
  // Tags where the opposite category dominates are pushed to the end.
  const getSortedTags = useCallback(
    (
      pool: TagPool,
      sortMode: SortMode,
      category: StandoutCategory
    ): TagWithStats[] => {
      const poolTags = tagsByPool[pool] ?? []
      const countKey = CATEGORY_COUNT_KEY[category]
      const oppositeKey = OPPOSITE_COUNT_KEY[category]

      const oppositeRank = (t: TagWithStats) => {
        if (!oppositeKey) return 0
        const own = t[countKey] as number
        const opp = t[oppositeKey] as number
        return opp > own ? 1 : 0
      }

      switch (sortMode) {
        case "frequency":
          return orderBy(
            poolTags,
            [oppositeRank, countKey, "text"],
            ["asc", "desc", "asc"]
          )
        case "recent":
          return orderBy(
            poolTags,
            [oppositeRank, (t) => t.lastUsedAt ?? "", "text"],
            ["asc", "desc", "asc"]
          )
        case "oldest":
          return orderBy(
            poolTags,
            [oppositeRank, (t) => t.lastUsedAt ?? "9999", "text"],
            ["asc", "asc", "asc"]
          )
        case "az":
          return orderBy(poolTags, [oppositeRank, "text"], ["asc", "asc"])
        default:
          return poolTags
      }
    },
    [tagsByPool]
  )

  // Auto-select top tags by count for a given category
  const getAutoSelectedTagIds = useCallback(
    (pool: TagPool, category: StandoutCategory, limit = 10): Uuid[] => {
      const poolTags = tagsByPool[pool] ?? []
      const countKey = CATEGORY_COUNT_KEY[category]
      const oppositeKey = OPPOSITE_COUNT_KEY[category]

      return orderBy(poolTags, [countKey], ["desc"])
        .filter((t) => {
          const count = t[countKey] as number
          if (count <= 0) return false
          if (oppositeKey && (t[oppositeKey] as number) > count) return false
          return true
        })
        .slice(0, limit)
        .map((t) => t.id)
    },
    [tagsByPool]
  )

  const createCustomTag = useCallback(
    async (text: string, pool: TagPool): Promise<Uuid | null> => {
      const normalized = text.trim().replace(/\s+/g, " ")
      if (!normalized) return null

      const existing = allTags.find(
        (t) =>
          t.pool === pool &&
          t.text.trim().toLowerCase() === normalized.toLowerCase()
      )
      if (existing) return existing.id

      const now = new Date().toISOString()
      const newTag: Omit<EvaluationKeywordConstructor, "id"> & { id?: Uuid } = {
        pool,
        text: normalized,
        isSeeded: false,
        isActive: true,
        createdBy: currentUserId!,
        createdAt: now,
        lastUpdatedAt: now,
      }

      const id =
        await Firebase.getInstance().addDocument<EvaluationKeywordConstructor>(
          DOC_TYPES.EVALUATION_KEYWORDS,
          newTag
        )
      return id
    },
    [allTags, currentUserId]
  )

  const saveStandoutMoment = useCallback(
    async (
      category: StandoutCategory | undefined,
      tagIds: Uuid[],
      options?: {
        note?: string
        generatedText?: string
        evaluationId?: Uuid
        classId?: Uuid
        projectId?: Uuid
        classSessionId?: Uuid
      }
    ): Promise<Uuid | null> => {
      if (!currentUserId) return null
      const moment = buildNewStandoutMoment({
        studentId,
        semester,
        curriculumId,
        createdBy: currentUserId,
        createdAt: new Date().toISOString(),
        category,
        tagIds,
        note: options?.note,
        generatedText: options?.generatedText,
        evaluationId: options?.evaluationId,
        classId: options?.classId,
        projectId: options?.projectId,
        classSessionId: options?.classSessionId,
      })
      if (!moment) return null

      const id =
        await Firebase.getInstance().addDocument<StandoutMomentConstructor>(
          DOC_TYPES.STANDOUT_MOMENTS,
          moment
        )

      return id
    },
    [studentId, semester, curriculumId, currentUserId]
  )

  const getMostRecentMomentsByCategory = useCallback(() => {
    const result: Partial<Record<StandoutCategory, StandoutMomentConstructor>> =
      {}

    const sorted = orderBy(studentMoments, "createdAt", "desc")
    for (const moment of sorted) {
      if (!moment.category || result[moment.category]) continue
      result[moment.category] = moment
    }

    return result
  }, [studentMoments])

  const momentCountsByCategory = useMemo(() => {
    return countBy(studentMoments, "category") as Partial<
      Record<StandoutCategory, number>
    >
  }, [studentMoments])

  const getRecentMomentContext = useCallback(
    (category: StandoutCategory, limit = 5): string[] => {
      const categoryMoments = orderBy(
        studentMoments.filter((m) => m.category === category),
        "createdAt",
        "desc"
      ).slice(0, limit)

      return categoryMoments.map((m) => {
        const tagTexts = (m.tagIds ?? [])
          .map((id) => tagsById[id]?.text)
          .filter(Boolean)
        return tagTexts.join(", ")
      })
    },
    [studentMoments, tagsById]
  )

  return {
    isFulfilled,
    allTags,
    tagsById,
    tagsByPool,
    tagStats,
    enrichedTags,
    getSortedTags,
    getAutoSelectedTagIds,
    createCustomTag,
    saveStandoutMoment,
    studentMoments,
    momentCountsByCategory,
    getMostRecentMomentsByCategory,
    getRecentMomentContext,
  }
}
